/**
 * Vague 20 — Hook + provider pour la vue active
 *
 * Fournit la liste des modules à afficher selon la vue active du user.
 * Recharge automatiquement quand la vue change.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ActiveViewCtx = createContext({
  view: null, modules: [], loading: true, refresh: () => {}, setActiveView: () => {},
});

export function ActiveViewProvider({ children }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/workspace/views/active");
      setView(r.data?.view || null);
    } catch {
      setView(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setActiveView = useCallback(async (viewId) => {
    setLoading(true);
    try {
      await api.post(`/workspace/views/${viewId}/activate`);
      await refresh();
    } catch (e) {
      setLoading(false);
      throw e;
    }
  }, [refresh]);

  const modules = (view?.modules || []).filter((m) => m.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <ActiveViewCtx.Provider value={{ view, modules, loading, refresh, setActiveView }}>
      {children}
    </ActiveViewCtx.Provider>
  );
}

export function useActiveView() {
  return useContext(ActiveViewCtx);
}

/**
 * Helper : pour une clé module donnée, retourne le slug d'URL canonique.
 * Pour un groupe, retourne le slug du groupe (ex: "g-gravites" → "/gravites").
 */
export const GROUP_KEY_TO_SLUG = {
  "g-gravites": "gravites",
  "g-risque-conformite": "risque-conformite",
  "g-capital": "capital",
  "g-cartographies": "cartographies",
  "g-origination": "origination-hub",
};

export const MODULE_KEY_TO_PATH = {
  "executive": "/app/executive",
  "score": "/app/score",
  "dashboard": "/app/dashboard",
  "leads": "/app/leads",
  "origination": "/app/origination",
  "negociation": "/app/negociation",
  "asymetries": "/app/asymetries",
  "positionnement": "/app/positionnement",
  "annuaire": "/app/annuaire",
  "documents": "/app/documents",
  "risques": "/app/risques",
  "controles": "/app/controles",
  "audits": "/app/audits",
  "incidents": "/app/incidents",
  "alertes": "/app/alertes",
  "plans": "/app/plans",
  "fournisseurs": "/app/fournisseurs",
  "financement": "/app/financement",
  "sources-capital": "/app/sources-capital",
  "courtage": "/app/courtage",
  "immobilier": "/app/immobilier",
  "rh": "/app/rh",
  "problem-gravity": "/app/problem-gravity",
  "operational-gravity": "/app/operational-gravity",
  "chainage": "/app/chainage",
  "utility-mapping": "/app/utility-mapping",
  "ontologie": "/app/ontologie",
  "intelligence": "/app/intelligence",
  "doctrine": "/app/doctrine",
  "trend": "/app/trend",
  "radar": "/app/radar",
  "briefs-library": "/app/briefs-library",
  "digests": "/app/digests",
  "analyses": "/app/analyses",
  "filtres": "/app/filtres",
  "bouteille-a-la-mer": "/app/bouteille-a-la-mer",
  "chat": "/app/chat",
  "brvm": "/app/brvm",
  "cartographie-360-index": "/app/cartographies/index",
};

export const MODULE_KEY_TO_LABEL = {
  "executive": "Executive 360°",
  "score": "Note de pilotage",
  "dashboard": "Dashboard",
  "leads": "Leads",
  "origination": "Origination",
  "negociation": "Négociation",
  "asymetries": "Asymétries",
  "positionnement": "Positionnement",
  "annuaire": "Annuaire",
  "documents": "Documents",
  "risques": "Risques",
  "controles": "Contrôles",
  "audits": "Audits",
  "incidents": "Incidents",
  "alertes": "Alertes",
  "plans": "Plans d'action",
  "fournisseurs": "Fournisseurs",
  "financement": "Financement",
  "sources-capital": "Sources de capital",
  "courtage": "Courtage",
  "immobilier": "Immobilier",
  "rh": "RH",
  "problem-gravity": "Gravité des problèmes",
  "operational-gravity": "Gravité opérationnelle",
  "chainage": "Chaînage causal",
  "utility-mapping": "Cartographie des dépendances",
  "ontologie": "Ontologie",
  "intelligence": "Intelligence",
  "doctrine": "Doctrine",
  "trend": "Veille",
  "radar": "Noam Radar",
  "briefs-library": "Briefs",
  "digests": "Digests",
  "analyses": "Analyses",
  "filtres": "Mes filtres",
  "bouteille-a-la-mer": "Bouteille à la mer",
  "chat": "Chat",
  "brvm": "Market Screener",
  "cartographie-360-index": "Cartographie 360°",
};
